import { useMutation, useSuspenseQuery } from "@apollo/client/react";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ActivateDataImportProjectDocument,
  type ActivateDataImportProjectMutation,
  type ActivateDataImportProjectMutationVariables,
  DeactivateDataImportProjectDocument,
  type DeactivateDataImportProjectMutation,
  type DeactivateDataImportProjectMutationVariables,
  GetDipDetailDocument,
  GetDipLifecycleDocument,
  type GetDipLifecycleQuery,
  type GetDipLifecycleQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

type DipLifecycleContextValue = {
  projectId: string;
  projectName: string;
  deactivatedAt: string | null;
  isReadOnly: boolean;
  canExecuteMutation: () => boolean;
  processing: boolean;
  activate: () => Promise<boolean>;
  deactivate: () => Promise<boolean>;
};

const DipLifecycleContext = createContext<DipLifecycleContextValue | null>(null);

type DipLifecycleProviderProps = {
  projectId: string;
  children: ReactNode;
};

export function DipLifecycleProvider({ projectId, children }: DipLifecycleProviderProps) {
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const processingRef = useRef(false);
  const mutationGuardRef = useRef({ projectId, canExecute: false });
  if (mutationGuardRef.current.projectId !== projectId) {
    mutationGuardRef.current = { projectId, canExecute: false };
  }
  const { data } = useSuspenseQuery<GetDipLifecycleQuery, GetDipLifecycleQueryVariables>(GetDipLifecycleDocument, {
    variables: { id: projectId },
    fetchPolicy: "cache-and-network",
  });
  const refetchQueries = [
    { query: GetDipLifecycleDocument, variables: { id: projectId } },
    { query: GetDipDetailDocument, variables: { id: projectId } },
  ];
  const [activateDataImportProject] = useMutation<
    ActivateDataImportProjectMutation,
    ActivateDataImportProjectMutationVariables
  >(ActivateDataImportProjectDocument, {
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [deactivateDataImportProject] = useMutation<
    DeactivateDataImportProjectMutation,
    DeactivateDataImportProjectMutationVariables
  >(DeactivateDataImportProjectDocument, {
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const beginProcessing = useCallback(() => {
    if (processingRef.current) return false;

    processingRef.current = true;
    setProcessing(true);
    return true;
  }, []);

  const endProcessing = useCallback(() => {
    processingRef.current = false;
    setProcessing(false);
  }, []);

  const activate = useCallback(async () => {
    if (!beginProcessing()) return false;

    try {
      await activateDataImportProject({ variables: { input: { id: projectId, tenantId } } });
      toast.success(t("feature.common.activatedSuccessfully", { model: "dataImportProject" }));
      return true;
    } catch (error) {
      toast.error(translateError(t, error));
      return false;
    } finally {
      endProcessing();
    }
  }, [activateDataImportProject, beginProcessing, endProcessing, projectId, t, tenantId]);

  const deactivate = useCallback(async () => {
    if (!beginProcessing()) return false;

    try {
      await deactivateDataImportProject({ variables: { input: { id: projectId, tenantId } } });
      toast.success(t("feature.common.deactivatedSuccessfully", { model: "dataImportProject" }));
      return true;
    } catch (error) {
      toast.error(translateError(t, error));
      return false;
    } finally {
      endProcessing();
    }
  }, [beginProcessing, deactivateDataImportProject, endProcessing, projectId, t, tenantId]);

  const canExecuteMutation = useCallback(
    () => mutationGuardRef.current.projectId === projectId && mutationGuardRef.current.canExecute,
    [projectId],
  );

  const project = data.dataImportProjectsByPk;
  const isCurrentProject = project?.id === projectId;
  const deactivatedAt = isCurrentProject ? (project.deactivatedAt ?? null) : null;
  mutationGuardRef.current = {
    projectId,
    canExecute: isCurrentProject && deactivatedAt == null,
  };
  const value = useMemo<DipLifecycleContextValue | null>(() => {
    if (!project || !isCurrentProject) return null;

    return {
      projectId,
      projectName: project.name,
      deactivatedAt,
      isReadOnly: deactivatedAt != null,
      canExecuteMutation,
      processing,
      activate,
      deactivate,
    };
  }, [activate, canExecuteMutation, deactivate, deactivatedAt, isCurrentProject, processing, project, projectId]);

  if (!value) return null;

  return <DipLifecycleContext.Provider value={value}>{children}</DipLifecycleContext.Provider>;
}

export function useDipLifecycle(): DipLifecycleContextValue {
  const context = useContext(DipLifecycleContext);
  if (!context) {
    throw new Error("useDipLifecycle must be used within DipLifecycleProvider");
  }
  return context;
}
